// Optional full VIN decode via NHTSA's free, public vPIC API.
//
// Everything in vinDecode.js is local — but exact make/model/trim/engine
// isn't derivable from the VIN alone; it's only known to each manufacturer.
// NHTSA maintains that as a public database and exposes it with no API key
// and no rate limit. This module is the ONLY place in the VIN Decoder tool
// that sends anything over the network, and it only runs when the user
// explicitly presses "Full decode" — never automatically.
//
// The only thing transmitted is the VIN itself, as part of the request URL.

const ENDPOINT = 'https://vpic.nhtsa.dot.gov/api/vehicles/decodevinvalues';

// A representative subset of the ~150 fields NHTSA returns — the ones an
// investigator is actually likely to want, in display order.
export const RESULT_FIELDS = [
  { key: 'Make', label: 'Make' },
  { key: 'Model', label: 'Model' },
  { key: 'ModelYear', label: 'Model year' },
  { key: 'Manufacturer', label: 'Manufacturer' },
  { key: 'VehicleType', label: 'Vehicle type' },
  { key: 'BodyClass', label: 'Body class' },
  { key: 'DriveType', label: 'Drive type' },
  { key: 'EngineCylinders', label: 'Engine cylinders' },
  { key: 'DisplacementL', label: 'Displacement (L)' },
  { key: 'FuelTypePrimary', label: 'Fuel type' },
  { key: 'TransmissionStyle', label: 'Transmission' },
  { key: 'GVWR', label: 'Gross vehicle weight rating' },
  { key: 'PlantCity', label: 'Plant city' },
  { key: 'PlantState', label: 'Plant state' },
  { key: 'PlantCountry', label: 'Plant country' },
  { key: 'ErrorText', label: 'NHTSA notes' },
];

/**
 * @param {string} vin
 * @returns {Promise<Record<string,string>>} the raw NHTSA result row
 */
export async function decodeVinNhtsa(vin) {
  const res = await fetch(`${ENDPOINT}/${encodeURIComponent(vin)}?format=json`);
  if (!res.ok) throw new Error(`NHTSA lookup failed (HTTP ${res.status}).`);

  const data = await res.json();
  const row = data?.Results?.[0];
  if (!row) throw new Error('NHTSA returned no result for this VIN.');

  // ErrorCode '0' means a clean decode; a non-zero code (often still paired
  // with usable data) means NHTSA itself flagged something about the VIN.
  if (row.ErrorCode && row.ErrorCode !== '0' && !row.Make) {
    throw new Error(row.ErrorText || `NHTSA could not decode this VIN (code ${row.ErrorCode}).`);
  }

  return row;
}
